<template>
  <!-- O resto — título, nível, `editable`, o `update:entries` — atravessa para a lista como atributo -->
  <ontos-colored-entry-list ref="list" :entries="states" form="dot" reorderable automatic-color :min="2"
                            :colors-of="colorsOf" :normalize="normalizeStates" :columns="columns"
                            :create-label="$tt('state-create')">
    <template v-if="$slots.subtitle" #subtitle>
      <slot name="subtitle"/>
    </template>
    <template v-if="$slots.actions" #actions>
      <slot name="actions"/>
    </template>
    <!-- Inicial e final se excluem, mas as duas caixas ficam sempre à vista: esconder uma faria o
         campo aparecer e desaparecer conforme o clique na outra. Na leitura, a palavra. -->
    <template #cell-flags="{entry, entries, set, commit, editable}">
      <!-- No telefone, um seletor curto no lugar das duas caixas, que empilhadas dobravam a altura da
           linha e tiravam a largura do nome (escolha do usuário). A inicial fica travada pelo mesmo
           motivo da caixa: o começo se escolhe em outra situação, e não se desmarca. -->
      <v-select v-if="editable && $vuetify.display.xs" :items="flagItems" :model-value="flagOf(entry, 'initial')"
                :disabled="!!entry.initial" density="compact" variant="underlined" hide-details
                @update:model-value="setFlag(entry, entries, set, commit, $event)"/>
      <div v-else-if="editable" class="checkbox-group">
        <v-checkbox density="compact" hide-details :label="$tt('state-initial')"
                    :model-value="entry.initial"
                    :disabled="!!entry.final || !!entry.initial"
                    @update:model-value="setInitial(entry, entries, commit)"/>
        <v-checkbox density="compact" hide-details :label="$tt('state-final')"
                    :model-value="entry.final" :disabled="!!entry.initial"
                    @update:model-value="set('final', !!$event)"/>
      </div>
      <span v-else class="text-body-2">{{ flagLabel(flagOf(entry)) }}</span>
    </template>
  </ontos-colored-entry-list>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ColoredEntryList, {ColoredEntry, EditableEntry, EntryChange, EntryColumn} from '@/components/shared/ColoredEntryList.vue'
import StateColorizer from '@/components/live/instance/StateColorizer'
import {State} from '@/types/Workflow'

/**
 * As situações de um fluxo — editáveis no `WorkflowInput`, só leitura na página de design do
 * ambiente, na da coleção e na do fluxo. O dono do que a situação tem de próprio na lista comum: a
 * bolinha (o semáforo, e não o chip da opção), as marcas Inicial/Final, a cor automática, o mínimo
 * de duas e a regra de um começo só. Nas quatro telas, a mesma resposta.
 */
@Component({
  name: 'ontos-state-list',
  components: {'ontos-colored-entry-list': ColoredEntryList}
})
class StateList extends Vue {
  declare $refs: { list: InstanceType<typeof ColoredEntryList> }

  @Prop({type: Array, required: true}) readonly states!: State[]

  /** Mais estreita no telefone, onde ela é o seletor, e não as duas caixas lado a lado. */
  get columns(): EntryColumn[] {
    return [{key: 'flags', label: this.$tt('state-flags') as string, width: this.$vuetify.display.xs ? '96px' : '190px'}]
  }

  /** Qual das marcas a situação tem: a mesma resposta para a leitura e para o seletor. */
  flagOf(state: State): 'initial' | 'final' | '' {
    return state.initial ? 'initial' : state.final ? 'final' : ''
  }

  flagLabel(flag: string): string {
    if (flag === 'initial') return this.$tt('state-initial') as string
    return flag === 'final' ? this.$tt('state-final') as string : ''
  }

  get flagItems() {
    return ['', 'initial', 'final'].map(value => ({value, title: this.flagLabel(value) || '—'}))
  }

  /**
   * A escolha do seletor, com as regras das caixas: Inicial passa a ser desta (e deixa de ser final),
   * Final e "—" marcam e desmarcam o final.
   */
  setFlag(state: EditableEntry, entries: EditableEntry[], set: (key: string, value: any) => void,
          commit: () => void, flag: string) {
    if (flag === 'initial') {
      state.final = false
      this.setInitial(state, entries, commit)
    } else {
      set('final', flag === 'final')
    }
  }

  /**
   * As cores em vigor, na ordem das linhas: a escolhida quando há, a derivada da ordem quando não —
   * o MESMO colorizador do Kanban e da ficha do item. Por POSIÇÃO, e não por id: a situação nova
   * ainda não tem id (ele nasce no servidor), e a posição é chave estável do mesmo jeito.
   */
  colorsOf(entries: ColoredEntry[]): string[] {
    const positioned = entries.map((entry, index) => ({...entry, id: index} as State))
    const colorizer = new StateColorizer(positioned)
    return positioned.map(state => colorizer.color(state.id))
  }

  /**
   * Marcar uma inicial desmarca a que estava, e desmarcar a própria não faz nada: qual é a situação
   * inicial é uma ESCOLHA, não um interruptor. Todo fluxo tem exatamente um começo — é o que o
   * servidor exige —, então deixar zerar só levaria a um erro na hora de salvar.
   */
  setInitial(state: EditableEntry, entries: EditableEntry[], commit: () => void) {
    entries.forEach(other => other.initial = other === state)
    commit()
  }

  /** Se a remoção levou junto a situação inicial, a primeira que sobrou assume. */
  normalizeStates(entries: EditableEntry[], change: EntryChange) {
    if (change.type !== 'remove') return
    const first = entries[0]
    if (first && !entries.some(entry => entry.initial)) {
      first.initial = true
      first.final = false
    }
  }

  validateModel(): boolean {
    return this.$refs.list.validateModel()
  }
}

export default toNative(StateList)
</script>

<style scoped>
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* As caixas trazem margem própria para ficarem na linha de um rótulo que aqui não existe; zerada,
   elas alinham com os campos ao lado em vez de descerem. */
.checkbox-group :deep(.v-checkbox) {
  margin-top: 0;
  padding-top: 0;
}
</style>
